import React, { useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Avatar,
  Box,
  Divider,
  Paper,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Logout as LogoutIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  Transgender as TransgenderIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../libs/auth';
import { useChangePasswordMutation } from './api/settingsApi';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0,0,0,0.05)',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0,0,0,0.1)'
  }
}));

const ProfilePage = () => {
  const { session, logOut } = useAuth();
  const user = session?.userInfo;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changePassword, { isLoading }] = useChangePasswordMutation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowOldPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      setSnackbar({ open: true, message: 'New passwords do not match!', severity: 'error' });
      return;
    }

    try {
      await changePassword({
        id: user?.id,
        body: {
          oldPassword,
          newPassword,
        },
      }).unwrap();
      setSnackbar({ open: true, message: 'Password changed successfully!', severity: 'success' });
      handleDialogClose();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to change password. Please try again.', severity: 'error' });
    }
  };


  return (
    <Box sx={{ 
      p: 4, 
      maxWidth: 800, 
      mx: 'auto',
      background: 'linear-gradient(to bottom right, #f9f9f9, #f0f4ff)',
      minHeight: '100vh'
    }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" sx={{ 
          fontWeight: 700,
          color: 'primary.main',
          flexGrow: 1
        }}>
          My Profile
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => setIsDialogOpen(true)}
          startIcon={<LockIcon />}
          sx={{ borderRadius: 2, px: 3, py: 1 }}
        >
          Change Password
        </Button>
      </Box>

      <StyledCard>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar sx={{ 
              width: 80, 
              height: 80, 
              mr: 3,
              bgcolor: 'primary.main',
              fontSize: '2.5rem'
            }}>
              {user?.firstName?.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                {user?.firstName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />


          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined" 
              color="error" 
              onClick={logOut}
              startIcon={<LogoutIcon />}
              sx={{ borderRadius: 2, px: 3, py: 1 }}
            >
              Sign Out
            </Button>
          </Box>
        </CardContent>
      </StyledCard>

      {/* Change Password Dialog */}
      <Dialog 
        open={isDialogOpen} 
        onClose={handleDialogClose}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          display: 'flex',
          alignItems: 'center'
        }}>
          <LockIcon sx={{ mr: 1 }} />
          Change Password
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <TextField
            label="Current Password"
            type={showOldPassword ? "text" : "password"}
            fullWidth
            margin="normal"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowOldPassword(!showOldPassword)}
                    edge="end"
                  >
                    {showOldPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="New Password"
            type={showNewPassword ? "text" : "password"}
            fullWidth
            margin="normal"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Confirm New Password"
            type={showConfirmPassword ? "text" : "password"}
            fullWidth
            margin="normal"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={newPassword !== confirmPassword && confirmPassword.length > 0}
            helperText={newPassword !== confirmPassword && confirmPassword.length > 0 ? "Passwords don't match" : ""}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleDialogClose}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handlePasswordChange} 
            disabled={isLoading || newPassword !== confirmPassword || !newPassword || !oldPassword || !confirmPassword} 
            variant="contained"
            startIcon={<EditIcon />}
            sx={{ borderRadius: 2 }}
          >
            {isLoading ? 'Updating...' : 'Update Password'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: 2, 
            display: 'flex', 
            alignItems: 'center',
            bgcolor: snackbar.severity === 'success' ? 'success.light' : 'error.light',
            color: snackbar.severity === 'success' ? 'success.contrastText' : 'error.contrastText'
          }}
        >
          {snackbar.severity === 'success' ? 
            <CheckCircleIcon sx={{ mr: 1 }} /> : 
            <CancelIcon sx={{ mr: 1 }} />}
          <Typography variant="body1">{snackbar.message}</Typography>
        </Paper>
      </Snackbar>
    </Box>
  );
};

export default ProfilePage;